import { DEFAULT_INSTRUMENT } from '../core/instruments.js';

// Minor-third sampling keeps the full piano within one semitone of a recording.
export const PIANO_PITCHES = Object.freeze(Array.from({ length: 30 }, (_, i) => 21 + i * 3));
export const pianoSamplePitch = pitch => Math.max(21, Math.min(108, 21 + Math.round((pitch - 21) / 3) * 3));
export const pianoSampleURL = pitch => `/sounds/salamander/${['C', '', '', 'Ds', '', '', 'Fs', '', '', 'A'][pitch % 12]}${Math.floor(pitch / 12) - 1}.mp3`;

export class PianoSamples {
  constructor(request = (...args) => fetch(...args)) { this.request = request; this.buffers = new Map(); this.pending = new Map(); }
  async load(context, pitches) {
    await Promise.all([...new Set(pitches.map(pianoSamplePitch))].map(pitch => {
      if (this.buffers.has(pitch)) return;
      if (!this.pending.has(pitch)) {
        const loading = Promise.resolve().then(async () => {
          try {
            const response = await this.request(pianoSampleURL(pitch), { signal: AbortSignal.timeout(15000) });
            if (!response.ok) throw new Error(`Sample ${pitch}: ${response.status}`);
            const buffer = await context.decodeAudioData(await response.arrayBuffer());
            this.buffers.set(pitch, buffer);
          } catch {
            throw new Error('Grand Piano could not load. Try again, or choose a synth sound.');
          } finally { this.pending.delete(pitch); }
        });
        this.pending.set(pitch, loading);
      }
      return this.pending.get(pitch);
    }));
  }
  get(pitch) { return this.buffers.get(pianoSamplePitch(pitch)); }
}
// AudioBuffers can be shared between playback and keyboard-preview contexts.
export const pianoSamples = new PianoSamples();

const PRESETS = {
  'electric-piano': { attack: .004, decay: 1.4, sustain: 0, release: .18, level: .22 },
  'warm-pad': { attack: .22, decay: .5, sustain: .8, release: .45, level: .085, wave: 'triangle', detune: 8, cutoff: 1800 },
  'synth-strings': { attack: .09, decay: .7, sustain: .75, release: .28, level: .065, wave: 'sawtooth', detune: 11, cutoff: 2800 },
  'analog-lead': { attack: .008, decay: 2, sustain: .65, release: .09, level: .09, wave: 'sawtooth', detune: 5, cutoff: 3600 },
  'synth-pluck': { attack: .003, decay: 5, sustain: 0, release: .09, level: .13, wave: 'square', detune: 3, cutoff: 4500 },
};

function envelope(param, start, length, age, level, preset) {
  const value = elapsed => level * Math.min(1, elapsed / preset.attack) * (preset.sustain + (1 - preset.sustain) * Math.exp(-preset.decay * Math.max(0, elapsed - preset.attack)));
  param.setValueAtTime(0, start);
  // Fade in resumed notes to prevent a discontinuity after seeking.
  const attack = Math.min(length / 3, age ? .008 : preset.attack);
  param.linearRampToValueAtTime(Math.max(.0001, value(age + attack)), start + attack);
  for (const point of [.3, 1, 3, length]) {
    if (point <= attack || point > length) continue;
    param.exponentialRampToValueAtTime(Math.max(.0001, value(age + point)), start + point);
  }
  param.linearRampToValueAtTime(0, start + length + preset.release);
}

export function createInstrumentVoice(context, output, instrument, note, { start, length, age }, samples, onEnded) {
  const gain = context.createGain(), nodes = [gain], sources = [];
  gain.connect(output);
  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    for (const node of [...sources, ...nodes]) node.disconnect();
    onEnded();
  };
  const voice = { stop() { for (const source of sources) { try { source.stop(); } catch {} } cleanup(); } };
  const velocity = Math.max(0, Math.min(1, note.velocity ?? .8));
  if (instrument === DEFAULT_INSTRUMENT) {
    const buffer = samples.get(note.pitch), ratio = 2 ** ((note.pitch - pianoSamplePitch(note.pitch)) / 12), offset = age * ratio;
    if (!buffer || offset >= buffer.duration) { gain.disconnect(); return null; }
    const source = context.createBufferSource(); sources.push(source);
    source.buffer = buffer; source.playbackRate.value = ratio; source.connect(gain);
    const level = .85 * velocity ** 1.35, release = .16;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(level, start + Math.min(.004, length / 3));
    gain.gain.setValueAtTime(level, start + length);
    gain.gain.linearRampToValueAtTime(0, start + length + release);
    source.onended = cleanup;
    source.start(start, offset); source.stop(start + length + release + .01);
    return voice;
  }
  const preset = PRESETS[instrument], frequency = 440 * 2 ** ((note.pitch - 69) / 12);
  const end = start + length + preset.release + .01;
  envelope(gain.gain, start, length, age, preset.level * velocity, preset);
  const oscillator = (type, detune = 0, multiplier = 1) => {
    const source = context.createOscillator(); sources.push(source);
    source.type = type; source.frequency.value = frequency * multiplier; source.detune.value = detune;
    return source;
  };
  if (instrument === 'electric-piano') {
    const carrier = oscillator('sine'), modulator = oscillator('sine', 0, 2), modulation = context.createGain(); nodes.push(modulation);
    envelope(modulation.gain, start, length, age, frequency * (1 + velocity * 3), { ...preset, decay: 4 });
    modulator.connect(modulation); modulation.connect(carrier.frequency); carrier.connect(gain);
  } else {
    const filter = context.createBiquadFilter(); nodes.push(filter); filter.type = 'lowpass'; filter.Q.value = .7;
    const cutoff = Math.min(context.sampleRate * .45, Math.max(frequency * 2, preset.cutoff * (.45 + velocity * .55)));
    filter.frequency.setValueAtTime(cutoff, start);
    if (instrument === 'synth-pluck') filter.frequency.exponentialRampToValueAtTime(Math.max(100, cutoff * .15), start + Math.min(.25, length));
    filter.connect(gain);
    oscillator(preset.wave, -preset.detune).connect(filter);
    oscillator(preset.wave, preset.detune).connect(filter);
  }
  sources[0].onended = cleanup;
  for (const source of sources) { source.start(start); source.stop(end); }
  return voice;
}
